/**
 * Extract column references from a math.js derive expression's AST.
 *
 * Used to populate `referencedColumns` on
 * {@link DerivedColumnAllNullError} — when a derive evaluates to null for
 * every row, the agent gets the list of columns the expression touched as
 * actionable feedback.
 *
 * The walker classifies every identifier-bearing node in a math.js AST as
 * one of:
 *
 *   - **column reference** — a SymbolNode that's not a math.js identifier,
 *     not a timeseries fetcher, not the `row` wrapper, and not in
 *     function-callee position. Collected with its source-level rendering.
 *   - **bracket / dot lookup on `row`** — `row.foo` and `row["foo.bar"]`
 *     parse identically in math.js to `AccessorNode(SymbolNode('row'),
 *     IndexNode([ConstantNode('foo')]))`. Both look up the *literal*
 *     property `foo` on the row wrapper — they are NOT path traversals.
 *     The constant-string dimension is the column name.
 *   - **member access on a column value** — `wellString.externalId` parses
 *     as `AccessorNode(SymbolNode('wellString'), IndexNode([ConstantNode(
 *     'externalId')]))`. Only the root SymbolNode (`wellString`) is
 *     reported; the accessor name is JS-style property access on the
 *     column's value, not a column name itself.
 *   - **exempt** — the `row` wrapper, timeseries fetcher names, and any
 *     identifier math.js itself knows about (constants like `pi`,
 *     functions like `sqrt` / `mean` / `sum`). Skipped.
 *
 * Note: this module does not pre-validate references. Pre-flight
 * validation was tried and removed because it duplicated coverage
 * already provided by (a) math.js's per-row "undefined symbol" errors
 * flowing into `result.errors` for bare-name typos, and (b) the all-null
 * detector for silent-undefined cases (`row["..."]` against a row
 * missing that key). The remaining responsibility — surfacing column
 * refs for error messages — does not depend on getting math.js's
 * resolution rules exactly right, so it stays robust to pipeline
 * composition (synth-derive hoisting, custom imports, future operators).
 */
import type { MathNode } from 'mathjs';

import { math } from './math-config.ts';
import { TIMESERIES_FETCHER_NAMES } from './timeseries-prefetch.ts';

const ROW_WRAPPER = 'row';
const FETCHER_NAMES_SET = new Set<string>(TIMESERIES_FETCHER_NAMES);

/** One column reference collected from a derive expression's AST. */
export interface ExpressionColumnRef {
  /** The column name to validate against `availableColumns`. */
  readonly name: string;
  /**
   * Original source-level rendering of the reference — `wellString`,
   * `wellString.externalId`, `row["foo.bar"]`. Surfaced in error messages
   * so the LLM sees its own syntax verbatim.
   */
  readonly source: string;
}

/**
 * Walk a math.js AST and collect every column reference that should be
 * validated against the available column set.
 *
 * Returns an empty array if the expression fails to parse — compilation
 * errors are surfaced separately by {@link evaluateDerivedFields}, so this
 * pass intentionally does not throw on parse failure.
 */
export function collectColumnRefsFromExpression(expression: string): ExpressionColumnRef[] {
  let root: MathNode;
  try {
    root = math.parse(expression) as MathNode;
  } catch {
    return [];
  }

  const refs: ExpressionColumnRef[] = [];
  const seen = new Set<string>();
  const push = (name: string, source: string): void => {
    const key = `${name}\x00${source}`;
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    refs.push({ name, source });
  };

  visit(root, refs, push);
  return refs;
}

function visit(node: MathNode, refs: ExpressionColumnRef[], push: (name: string, source: string) => void): void {
  const type = node.type;

  if (type === 'SymbolNode') {
    const name = (node as unknown as { name: string }).name;
    if (isExemptIdentifier(name)) {
      return;
    }
    push(name, name);
    return;
  }

  if (type === 'AccessorNode') {
    const accessor = node as unknown as {
      object: MathNode;
      index: { dimensions: MathNode[] };
    };
    const obj = accessor.object;

    // `row.foo` / `row["foo.bar"]` — math.js parses both identically.
    // The constant-string dimension is a flat-key lookup on the row.
    if (isRowSymbol(obj)) {
      const key = constantStringDim(accessor.index);
      if (key !== undefined) {
        push(key, `row["${key}"]`);
        return;
      }
      // Dynamic index (`row[someVar]`) — the bracket key isn't a static
      // string we can match against the schema, but the dimension
      // expression may itself contain column refs we can validate. Recurse
      // into the index so e.g. `someVar` in `row[someVar]` gets checked.
      for (const dim of accessor.index.dimensions) {
        visit(dim, refs, push);
      }
      return;
    }

    // Generic accessor chain (`wellString.externalId`, `obj[k]`). Recurse
    // into the object — only the root SymbolNode is a column ref. The
    // dimensions are property names / index expressions on the column's
    // value, not columns themselves.
    visit(obj, refs, push);
    return;
  }

  if (type === 'FunctionNode') {
    // The `fn` field is the function name (a SymbolNode), resolved against
    // math.js's registered functions / fetchers — not a column. Recurse
    // only into args.
    const fnNode = node as unknown as { args: MathNode[] };
    for (const arg of fnNode.args) {
      visit(arg, refs, push);
    }
    return;
  }

  // Fallback: generic recursion via math.js's `forEach` (every MathNode
  // exposes it). Covers OperatorNode, ConditionalNode, ArrayNode,
  // ObjectNode, RangeNode, BlockNode, ParenthesisNode, etc. without
  // hand-coding each case.
  const generic = node as unknown as { forEach?: (cb: (child: MathNode) => void) => void };
  if (typeof generic.forEach === 'function') {
    generic.forEach((child) => {
      visit(child, refs, push);
    });
  }
}

function isRowSymbol(node: MathNode): boolean {
  return node.type === 'SymbolNode' && (node as unknown as { name: string }).name === ROW_WRAPPER;
}

/**
 * If `index` is the IndexNode of a single-dimension constant-string lookup
 * (`["foo"]` / `.foo`), return that string. Otherwise (dynamic index,
 * non-string constant, multi-dim) return undefined — caller defers to
 * runtime.
 */
function constantStringDim(index: { dimensions: MathNode[] }): string | undefined {
  if (index.dimensions.length !== 1) {
    return undefined;
  }
  const dim = index.dimensions[0];
  if (dim === undefined || dim.type !== 'ConstantNode') {
    return undefined;
  }
  const value = (dim as unknown as { value: unknown }).value;
  return typeof value === 'string' ? value : undefined;
}

function isExemptIdentifier(name: string): boolean {
  if (name === ROW_WRAPPER) {
    return true;
  }
  if (FETCHER_NAMES_SET.has(name)) {
    return true;
  }
  // math.js exposes every registered identifier (constants like `pi`, `e`,
  // `Infinity`, `true`/`false`/`null`; functions like `sqrt`, `mean`,
  // `sum`, custom imports from math-config) as a property on the math
  // object. `name in math` cleanly partitions math.js-known identifiers
  // from data columns.
  return name in math;
}
